import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { sitesApi } from '../../components/sites/sitesApi';

const card = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  padding: '1.25rem 1.5rem',
  marginBottom: '0.75rem',
};

const primaryBtn = {
  display: 'inline-block',
  padding: '0.75rem 1.5rem',
  background: 'var(--ink, #171A0D)',
  color: '#fff',
  border: 'none',
  borderRadius: '999px',
  fontSize: '1rem',
  fontWeight: 600,
  textDecoration: 'none',
  cursor: 'pointer',
};

const secondaryBtn = {
  ...primaryBtn,
  background: '#fff',
  color: 'var(--ink, #171A0D)',
  border: '1px solid #d1d5db',
  marginLeft: '0.5rem',
};

export default function SitesList() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const workspaceId = selectedWorkspaceId || currentUser?.uid;
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [finishingId, setFinishingId] = useState(null);
  const [finishError, setFinishError] = useState('');

  const loadSites = useCallback(async () => {
    if (!workspaceId) {
      setSites([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setListError('');
    try {
      const res = await sitesApi.listSites(workspaceId);
      setSites(Array.isArray(res.sites) ? res.sites : []);
    } catch (e) {
      setSites([]);
      setListError(e?.message || 'We could not load your sites. Please try again in a minute.');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  async function finishBuilding(site) {
    if (!workspaceId || !site?.siteId) return;
    setFinishError('');
    setFinishingId(site.siteId);
    try {
      const look = site.look;
      const res = await sitesApi.renderSite(workspaceId, site.siteId, { look });
      if (!res.previewUrl) {
        setFinishError('We could not build the preview. Please try again in a minute.');
        return;
      }
      navigate(`/sites/${site.siteId}`, {
        state: { previewUrl: res.previewUrl, workspaceId },
      });
    } catch (e) {
      setFinishError(e?.message || 'We could not build the preview. Please try again in a minute.');
    } finally {
      setFinishingId(null);
    }
  }

  return (
    <div style={{ padding: '2rem 1.5rem', maxWidth: 960, margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.75rem', marginBottom: '0.5rem', color: 'var(--ink, #171A0D)' }}>Sites</h1>
      <p style={{ color: 'var(--muted, #5B614E)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
        Build a new site from your current website or a few questions. Preview first, confirm every fact, then publish when you are ready.
      </p>
      <div style={{ marginBottom: '1.5rem' }}>
        <button type="button" style={primaryBtn} onClick={() => navigate('/sites/new')}>
          New site
        </button>
      </div>
      {listError ? (
        <p style={{ color: '#b91c1c', marginBottom: '1rem' }} role="alert">
          {listError}
        </p>
      ) : null}
      {finishError ? (
        <p style={{ color: '#b91c1c', marginBottom: '1rem' }} role="alert">
          {finishError}
        </p>
      ) : null}
      <div style={{ ...card, padding: '1.5rem 2rem' }}>
        <h2 style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>Your sites</h2>
        {loading ? (
          <p style={{ color: '#6b7280' }}>Loading…</p>
        ) : listError ? (
          <p style={{ color: '#6b7280' }}>Your sites could not be loaded.</p>
        ) : sites.length === 0 ? (
          <p style={{ color: '#6b7280' }}>Start your first site in a minute.</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {sites.map((site) => {
              const needsFinish = site.status === 'draft' && !site.previewUrl;
              const title = site.businessName || 'Untitled site';
              return (
                <li key={site.siteId} style={card}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ flex: '1 1 200px' }}>
                      <strong style={{ fontSize: '1rem' }}>{title}</strong>
                      {needsFinish ? (
                        <p style={{ color: '#6b7280', margin: '0.35rem 0 0', fontSize: '0.875rem' }}>
                          Draft · preview not ready yet
                        </p>
                      ) : (
                        <p style={{ color: '#6b7280', margin: '0.35rem 0 0', fontSize: '0.875rem' }}>
                          {site.status === 'draft' ? 'Draft' : site.status || 'Draft'}
                        </p>
                      )}
                    </div>
                    <div>
                      {needsFinish ? (
                        <button
                          type="button"
                          style={primaryBtn}
                          disabled={finishingId === site.siteId}
                          onClick={() => finishBuilding(site)}
                        >
                          {finishingId === site.siteId ? 'Building…' : 'Finish building'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          style={secondaryBtn}
                          onClick={() =>
                            navigate(`/sites/${site.siteId}`, {
                              state: { previewUrl: site.previewUrl, workspaceId },
                            })
                          }
                        >
                          Open editor
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
